/** @jsxImportSource @emotion/react */

import { css } from "@emotion/react";

import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import { useDrawerStore } from "@/stores/useDrawerStore";
import Typography from "@mui/material/Typography";

import {
  colors,
  CorporateFareIcon,
  DashboardOutlineIcon,
  DriverIcon,
  FileDraftOutlineIcon,
  GroupOutlineIcon,
  IntegrationIcon,
  TruckOutlineIcon
} from "@dotnet/telematics-design-system";

import { NAVIGATION_ITEMS, ROUTES, useMediaQueries } from "../../../../shared";
import Tooltip from "../../../Tooltip/Tooltip";
import useTutorialMode from "../../../TutorialMode/hooks/useTutorialMode";
import { STEP_GROUP } from "../../../TutorialMode/types";
import { useStyles } from "../../styles";

interface ILinkItemProps {
  name: NAVIGATION_ITEMS;
  link: ROUTES;
}

const LinkItem = ({ name, link }: ILinkItemProps) => {
  const { isTutorialOpen, stepGroup, setTutorialDetails } = useTutorialMode();
  const { t } = useTranslation();
  const { toMd } = useMediaQueries();

  const { isDesktopDrawerOpen, isMobileDrawerOpen } = useDrawerStore();

  const { navigationItem } = useStyles();

  const resource = globalThis.location.pathname;
  const vehiclesPage = resource === ROUTES.Home && link === ROUTES.Vehicles;

  const getUrlLink = () => {
    if (resource === ROUTES.DriverFiles && name === NAVIGATION_ITEMS.Files) {
      return ROUTES.DriverFiles;
    }

    if (
      resource === ROUTES.UsersInvitations &&
      name === NAVIGATION_ITEMS.Users
    ) {
      return ROUTES.UsersInvitations;
    }

    if (
      resource === ROUTES.IntegrationsApi &&
      name === NAVIGATION_ITEMS.Integrations
    ) {
      return ROUTES.IntegrationsApi;
    }

    if (
      resource === ROUTES.AnalysisProviders &&
      name === NAVIGATION_ITEMS.Integrations
    ) {
      return ROUTES.AnalysisProviders;
    }

    return link;
  };

  const urlLInk = getUrlLink();
  const isActive = resource === urlLInk || vehiclesPage;

  const iconToRender = (item: NAVIGATION_ITEMS) => {
    switch (item) {
      case NAVIGATION_ITEMS.Dashboard:
        return <DashboardOutlineIcon />;
      case NAVIGATION_ITEMS.Vehicles:
        return <TruckOutlineIcon />;
      case NAVIGATION_ITEMS.Drivers:
        return <DriverIcon />;
      case NAVIGATION_ITEMS.Files:
        return <FileDraftOutlineIcon />;
      case NAVIGATION_ITEMS.Companies:
        return <CorporateFareIcon />;
      case NAVIGATION_ITEMS.Users:
        return <GroupOutlineIcon />;
      case NAVIGATION_ITEMS.Integrations:
        return <IntegrationIcon />;

      default:
        return null;
    }
  };

  const renderLinkWithConditionalTooltip = (children: any) => {
    if (!isMobileDrawerOpen && !isDesktopDrawerOpen) {
      return (
        <Tooltip
          title={t(`General##${name.toLowerCase()}`)}
          placement="right"
          size="medium"
        >
          {children}
        </Tooltip>
      );
    } else {
      return children;
    }
  };

  const handleClick = () => {
    const isCompaniesPage = resource === ROUTES.Companies;
    const isVehiclesPage = resource === ROUTES.Vehicles;

    if (link === ROUTES.Companies && isTutorialOpen) {
      setTutorialDetails({
        isTutorialOpen: isCompaniesPage,
        stepIndex: 2,
        stepGroup: stepGroup,
        isTutorialActive: true
      });
    }

    if (
      link === ROUTES.Vehicles &&
      isTutorialOpen &&
      stepGroup === STEP_GROUP.TutorialCreateSchedule
    ) {
      const nextStepIndex = toMd ? 3 : 2;

      setTutorialDetails({
        isTutorialOpen: isVehiclesPage,
        stepIndex: nextStepIndex,
        stepGroup: stepGroup,
        isTutorialActive: true
      });
    }

    if (
      link === ROUTES.Vehicles &&
      isTutorialOpen &&
      stepGroup === STEP_GROUP.TutorialApplySchedule
    ) {
      setTutorialDetails({
        isTutorialOpen: isVehiclesPage,
        stepIndex: toMd ? 2 : 1,
        stepGroup: stepGroup,
        isTutorialActive: true
      });
    }
  };

  return (
    <li>
      {renderLinkWithConditionalTooltip(
        <Link
          onClick={handleClick}
          data-testid={`${name}-navigation-item`}
          to={link}
          id={name}
          css={css([
            {
              color: colors.gray700,
              backgroundColor: isActive ? colors.gray200 : "transparent",

              "& > svg": {
                color: isActive ? colors.textDark : colors.gray700,
                flexShrink: 0
              },

              "&:hover": {
                backgroundColor: isActive ? colors.gray300 : colors.gray100
              },

              "&:active": {
                backgroundColor: isActive ? colors.gray300 : colors.gray200
              }
            },
            navigationItem
          ])}
        >
          {iconToRender(name)}
          <Typography
            variant="body2Medium"
            css={css({
              marginLeft: "8px",
              whiteSpace: "nowrap"
            })}
          >
            {t(`General##${name.toLowerCase()}`)}
          </Typography>
        </Link>
      )}
    </li>
  );
};

export default LinkItem;
